import { useEffect, useRef, useState } from 'react';

const TERMINAL_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'touchstart',
];

type UseTerminalScreensaverParams = {
  idleSeconds: number;
  hasMedia: boolean;
  enabled?: boolean;
};

export const useTerminalScreensaver = ({
  idleSeconds,
  hasMedia,
  enabled = true,
}: UseTerminalScreensaverParams) => {
  const [isActive, setIsActive] = useState(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled || idleSeconds <= 0 || !hasMedia) {
      setIsActive(false);
      return;
    }

    lastActivityRef.current = Date.now();
    setIsActive(false);

    const markActivity = () => {
      lastActivityRef.current = Date.now();
      setIsActive(false);
    };

    TERMINAL_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true }),
    );

    const timer = window.setInterval(() => {
      const elapsed = (Date.now() - lastActivityRef.current) / 1000;
      if (elapsed >= idleSeconds) {
        setIsActive(true);
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
      TERMINAL_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
    };
  }, [enabled, idleSeconds, hasMedia]);

  const dismiss = () => {
    lastActivityRef.current = Date.now();
    setIsActive(false);
  };

  return { isScreensaverActive: isActive, dismissScreensaver: dismiss };
};
